import React, { useState, useEffect } from "react";
import { CiSearch } from "react-icons/ci";
import { ImCross } from "react-icons/im";
import { FaCheck } from "react-icons/fa";
import { BsArrowRightShort } from "react-icons/bs";
import {
  Table,
  Button,
  Spinner,
  OverlayTrigger,
  Tooltip,
  Col,
  Form,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

import {
  checkUnmappedMSBCFields,
  onSearchMappings,
  onEditMapping,
  onCancelMapping,
  onDeleteExistingMapping,
  onSaveEditedMappings,
  checkIsMappingsSaved,
  onChangeMappingsMetadata,
} from "../../store/slice/mappingSlice";
import { getUrl } from "../../helpers/url";
import { useCmsHook } from "../../helpers/cms/cmsHook";

import deleteIcon from "../../assets/images/delete.svg";
import editIcon from "../../assets/images/edit.svg";

import MappingDropdown from "./MappingDropdown";
import SearchResultsNotFound from "./SearchResultsNotFound";
import Pagination from "./Pagination";

function MappingWindow({
  mappings,
  stripeFields,
  msbcFields,
  mappingsFor,
  pathName,
  notificationPath,
}) {
  const { cmsInstance } = useCmsHook();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  // mapping states
  const {
    searchInput,
    searchResultsArray,
    currentlyEditing,
    selectPreferenceStripe,
    isEditMapping,
    slicedMappings,
    metadataPreference,
  } = useSelector((state) => state.mapping);
  // invoice states
  const {
    preferenceForProduct,
    productPreference,
    stripeProductSearchParameter,
    stripeProductMetadataPreference,
    msbcProductSearchParameter,
    preferenceForSearchParameters,
  } = useSelector((state) => state.invoice);

  const mappedList =
    (mappingsFor === "product" && mappings.length > 10
      ? slicedMappings
      : mappings
    )?.map((mappedItem) => {
      if (
        searchResultsArray.includes(mappedItem.id) ||
        searchInput.length === 0 ||
        !searchInput
      )
        return renderMappings({
          currentlyEditing,
          mappedItem,
          searchInput,
          stripeFields,
          selectPreferenceStripe,
          isEditMapping,
          mappingsFor,
          mappings: mappingsFor === "product" ? slicedMappings : mappings,
          metadataPreference,
          dispatch,
        });
      return null;
    }) ?? [];

  useEffect(() => {
    if (msbcFields && msbcFields.length > 0 && mappings) {
      dispatch(checkUnmappedMSBCFields({ mappingsFor }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappings, stripeFields, msbcFields]);

  return (
    <div>
      {mappingsFor !== "product" && <hr className="settings-horizontal-line" />}

      <div>
        {cmsInstance &&
          cmsInstance.showRichText(pathName, "mapping-window-header")}

        <div
          className="d-flex flex-row justify-content-between my-4"
          style={{ marginTop: "19px" }}
        >
          <div className="d-flex flex-row dropdown-search-box">
            <span className="my-auto">
              <CiSearch size={18} className="mx-2" />
            </span>
            <input
              type="search"
              placeholder="search.."
              className="w-100 dropdown-search"
              value={searchInput}
              onChange={(e) => {
                dispatch(
                  onSearchMappings({
                    enteredInput: e.target.value,
                    mappingsFor,
                  })
                );
              }}
            />
          </div>
        </div>

        {searchResultsArray.length === 0 && searchInput ? (
          <SearchResultsNotFound />
        ) : (
          <Table responsive className="mt-2">
            <thead className="mapping-header">
              <tr>
                <th style={{ width: "400px" }}>Business Central</th>
                <th style={{ width: "150px" }}></th>
                <th style={{ width: "400px" }}>STRIPE</th>
                <th style={{ textAlign: "right", width: "200px" }}>ACTIONS</th>
              </tr>
            </thead>

            <tbody className="mapping-body">{mappedList}</tbody>
          </Table>
        )}

        <div className="save-button-container mt-5">
          <Button
            className="save-changes-button"
            disabled={isEditMapping}
            onClick={() =>
              onSaveMappings({
                dispatch,
                setIsButtonLoading,
                mappings,
                mappingsFor,
                searchParams,
                cmsInstance,
                notificationPath,
                preferenceForProduct,
                productPreference,
                stripeProductSearchParameter,
                stripeProductMetadataPreference,
                msbcProductSearchParameter,
                preferenceForSearchParameters,
              })
            }
          >
            {isButtonLoading ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              <span>Save</span>
            )}
          </Button>
        </div>

        {mappingsFor === "product" && mappings.length > 10 && (
          <Pagination mappings={mappings} mappingsFor={mappingsFor} />
        )}
      </div>
    </div>
  );
}

export default MappingWindow;

function renderMappings({
  currentlyEditing,
  mappedItem,
  searchInput,
  stripeFields,
  selectPreferenceStripe,
  isEditMapping,
  mappingsFor,
  mappings,
  metadataPreference,
  dispatch,
}) {
  if (mappedItem.id === currentlyEditing && isEditMapping) {
    return (
      <tr style={{ verticalAlign: "middle" }} key={mappedItem.id}>
        <td>{mappedItem.Msbc_FieldName_HRF}</td>
        <td>
          <BsArrowRightShort
            style={{
              transform: "scaleX(-1)",
            }}
            className="mt-auto"
            size={25}
          />
        </td>
        <td>
          <Col lg={9}>
            <MappingDropdown
              mappings={mappings}
              stripeFields={stripeFields}
              mappedItem={mappedItem}
              mappingsFor={mappingsFor}
            />
            {((selectPreferenceStripe &&
              selectPreferenceStripe?.CRF === "key") ||
              mappedItem.Stripe_FieldName_HRF === "MetaData") && (
              <Form.Control
                type="text"
                placeholder="Metadata"
                className="meta-data-text-box mt-3"
                onChange={(e) =>
                  dispatch(
                    onChangeMappingsMetadata({
                      mappingsFor,
                      enteredInput: e.target.value,
                    })
                  )
                }
                value={metadataPreference[mappingsFor]}
              />
            )}
          </Col>
        </td>
        <td style={{ textAlign: "right" }}>
          <div>
            {((selectPreferenceStripe &&
              selectPreferenceStripe.HRF !== "MetaData") ||
              (selectPreferenceStripe &&
                selectPreferenceStripe.HRF === "MetaData" &&
                metadataPreference[mappingsFor].length > 0) ||
              mappedItem.Stripe_FieldName_HRF) && (
              <Button
                onClick={() => {
                  dispatch(
                    onSaveEditedMappings({ id: mappedItem.id, mappingsFor })
                  );
                }}
                className="mapping-icon-button me-2"
              >
                <FaCheck size={16} />
              </Button>
            )}
            <Button
              onClick={() => {
                dispatch(onCancelMapping());
              }}
              className="mapping-icon-button"
            >
              <ImCross size={16} />
            </Button>
          </div>
        </td>
      </tr>
    );
  } else {
    return (
      <tr style={{ verticalAlign: "middle" }} key={mappedItem.id}>
        <td>
          <span>{mappedItem.Msbc_FieldName_HRF}</span>
          <br></br>
          {mappingsFor === "product" && (
            <span style={{ fontSize: "12px", opacity: "0.9" }}>
              (Product Id: {mappedItem.Msbc_Field_Description})
            </span>
          )}
        </td>
        <td>
          <BsArrowRightShort
            style={{
              transform: "scaleX(-1)",
            }}
            className="mt-auto"
            size={25}
          />
        </td>
        <td>
          {mappedItem.Stripe_FieldName_HRF === undefined ? (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip style={{ fontSize: "11px" }}>
                  To add mapping for this field click on edit icon or leave as
                  it is to not map this field.
                </Tooltip>
              }
            >
              <span style={{ cursor: "pointer", color: "#7a91ab" }}>
                Not Mapped
              </span>
            </OverlayTrigger>
          ) : (
            <>
              <span>{mappedItem.Stripe_FieldName_HRF}</span>
              <br></br>
              {mappingsFor === "product" && (
                <span style={{ fontSize: "12px", opacity: "0.9" }}>
                  (Product Number: {mappedItem.Stripe_Field_Description})
                </span>
              )}
            </>
          )}
        </td>
        <td style={{ textAlign: "right" }}>
          <div>
            <button
              disabled={isEditMapping}
              onClick={() => {
                dispatch(
                  onEditMapping({
                    mappedItem,
                    mappingsFor,
                  })
                );
              }}
              className="me-2 mapping-icon-button"
            >
              <img src={editIcon} alt="edit icon" />
            </button>
            <button
              disabled={
                mappedItem.Msbc_Field_Required === true ||
                mappedItem.Stripe_FieldName_CRF === undefined
              }
              onClick={() =>
                dispatch(
                  onDeleteExistingMapping({ id: mappedItem.id, mappingsFor })
                )
              }
              className="mapping-icon-button"
              style={{
                opacity: `${
                  (mappedItem.Msbc_Field_Required === true ||
                    mappedItem.Stripe_FieldName_CRF === undefined) &&
                  "0.7"
                }`,
                cursor: `${
                  (mappedItem.Msbc_Field_Required === true ||
                    mappedItem.Stripe_FieldName_CRF === undefined) &&
                  "not-allowed"
                }`,
              }}
            >
              <img src={deleteIcon} alt="delete icon" />
            </button>
          </div>
        </td>
      </tr>
    );
  }
}

/**
 * This function is responsible for saving the mappings in the DB
 */
export async function onSaveMappings({
  dispatch,
  setIsButtonLoading,
  mappings,
  mappingsFor,
  searchParams,
  cmsInstance,
  notificationPath,
  preferenceForProduct,
  productPreference,
  stripeProductSearchParameter,
  stripeProductMetadataPreference,
  msbcProductSearchParameter,
  preferenceForSearchParameters,
}) {
  try {
    setIsButtonLoading(true);
    const account = searchParams.get("account");
    const validMappings = [];
    mappings.forEach((mappedItem) => {
      if (mappedItem.Stripe_FieldName_CRF !== undefined) {
        const newMapping = {
          Stripe_FieldName_CRF: mappedItem.Stripe_FieldName_CRF,
          Msbc_FieldName_HRF: mappedItem.Msbc_FieldName_HRF,
          Msbc_FieldName_CRF: mappedItem.Msbc_FieldName_CRF,
          Msbc_Field_Description: mappedItem.Msbc_Field_Description,
          Stripe_Field_Description: mappedItem.Stripe_Field_Description,
          Stripe_FieldType: mappedItem.Stripe_FieldType,
          Msbc_FieldType: mappedItem.Msbc_FieldType,
          Stripe_FieldName_HRF: mappedItem.Stripe_FieldName_HRF,
        };
        if (mappedItem.Msbc_Field_Required) {
          newMapping.Msbc_Field_Required = mappedItem.Msbc_Field_Required;
        }
        validMappings.push(newMapping);
      }
    });
    await axios.post(getUrl("REACT_APP_SAVE_MAPPINGS"), {
      account,
      mapping: validMappings,
      mappingObject: mappingsFor,
    });

    if (mappingsFor === "product") {
      await axios.post(getUrl("REACT_APP_SAVE_PREFERENCES"), {
        account,
        preferences: {
          product: {
            generateInvoice: {
              create:
                preferenceForProduct === "Use default product" ? true : false,
              mapping: {
                msbcDefaultProduct: productPreference,
              },
            },
            filterOptions: {
              filterStripeProperty:
                stripeProductSearchParameter.value === "key"
                  ? {
                      name: stripeProductSearchParameter.name,
                      value: stripeProductMetadataPreference,
                    }
                  : stripeProductSearchParameter,
              filterMsbcProperty: msbcProductSearchParameter,
            },
            searchParameters:
              preferenceForSearchParameters === "Select search parameters"
                ? false
                : true,
          },
        },
        preferenceType: "product",
      });
    }

    dispatch(checkIsMappingsSaved({ mappingsFor }));
    toast.success(
      cmsInstance &&
        cmsInstance.sendData().cmsContent.notifications.syncSettings
          .successContent[notificationPath].message
    );
  } catch (error) {
    console.log(error);
    let errorMessage;
    if (error.response && error.response.data.message)
      errorMessage = error.response.data.message;
    else errorMessage = error.message;
    toast.error(errorMessage);
  } finally {
    setIsButtonLoading(false);
  }
}
